import { ReactNode } from 'react';
import moment from 'moment';
import { Tag } from 'antd';
import TruckIconOutlined from 'components/icons/TruckIconOutlined';
import { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import useCodelists from 'hooks/useCodelists';
import { Manifest } from 'store/manifests/types';
import { getContainerStatusLabel } from 'utils/containerStatus';
import {
    ManifestIconWrapper,
    SummaryDetail,
    SummaryDetailValue,
    SummaryDetailsRow,
    SummaryReference,
    SummaryTitleRow,
    SummaryWrapper,
} from './styles';

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

// Same departure/arrival pairing as the manifest overview header. The departure port is a
// UN/LOCODE (CL244), which is never cached client-side, so it is shown as the stored code.
// The arrival port falls back to its stored code when the name cannot be resolved.
const getRoute = (manifest: Manifest, customsOffices: SelectOption[] | undefined) => {
    const departurePort = manifest.departurePort;
    const arrivalPort = manifest.declarationsData?.arrivalPort;

    const arrivalPortName = arrivalPort
        ? customsOffices?.find((office) => office.id === arrivalPort)?.value ?? arrivalPort
        : '-';

    return departurePort ? `${departurePort} - ${arrivalPortName}` : arrivalPortName;
};

const Detail = ({ label, value }: { label: string; value: ReactNode }) => (
    <SummaryDetail>
        <span>{label}</span>
        <SummaryDetailValue>{value}</SummaryDetailValue>
    </SummaryDetail>
);

type Props = {
    manifest: Manifest;
};

const ManifestSummary = ({ manifest }: Props) => {
    const { aisCodelists } = useCodelists();

    const containerIndicator = manifest.declarationsData?.containerIndicator;
    const movementType = containerIndicator == null ? undefined : getContainerStatusLabel(Number(containerIndicator));

    const departure = manifest.declarationsData?.dateTimeOfDeparture;

    return (
        <SummaryWrapper>
            <SummaryTitleRow>
                <ManifestIconWrapper>
                    <TruckIconOutlined />
                </ManifestIconWrapper>
                <SummaryReference>{manifest.referenceId || '-'}</SummaryReference>
                {movementType && <Tag style={{ marginInlineEnd: 0 }}>{movementType}</Tag>}
            </SummaryTitleRow>

            <SummaryDetailsRow>
                <Detail label="Route:" value={getRoute(manifest, aisCodelists?.nationalCustomsOffice)} />
                <Detail label="Departure:" value={departure ? moment(departure).format(DATE_TIME_FORMAT) : '-'} />
                <Detail label="Jobs:" value={manifest.jobs?.length ?? 0} />
            </SummaryDetailsRow>
        </SummaryWrapper>
    );
};

export default ManifestSummary;
